import './applySearchYearPage.scss';

import KeyboardBackspaceRoundedIcon from '@mui/icons-material/KeyboardBackspaceRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import ThemeProvider from '@mui/material/styles/ThemeProvider';
import { rangeRight, toString } from 'lodash';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { RadioGroup } from '../../../components/RadioGroup/RadioGroup';
import { RadioOptionClickEvent } from '../../../components/RadioGroup/types/RadioOptionClickEvent';
import { isValidClickEvent } from '../../../components/utils/isValidClickEvent';
import useApplyStore from '../../../store/applyStore/useApplyStore';
import useNavigationPaneStore from '../../../store/navigationPaneStore/useNavigationPaneStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): JSX.Element {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { next, previous } = useWorkflowStore();
  const { setDrawerOpen } = useNavigationPaneStore();

  const { year, setYear } = useApplyStore();

  const { register, handleSubmit, watch } = useForm({
    defaultValues: {
      yearValue: year,
    },
  });

  const YEARS_OF_VEHICLES_TO_SHOW = 20;
  const INCLUDE_NEXT_YEAR_OFFSET = 2;

  // Present 20 years of vehicles starting from this year whilst including next year.
  // lodash range does not include its end parameter, so we add 2 to include next year
  const vehicleYears = rangeRight(
    new Date().getFullYear() - YEARS_OF_VEHICLES_TO_SHOW,
    new Date().getFullYear() + INCLUDE_NEXT_YEAR_OFFSET,
  ).map(toString);

  const formSubmit = (values: FieldValues): void => {
    const { yearValue } = values;

    if (yearValue) {
      setYear(yearValue);

      navigate(next());
    }
  };

  const previousStep = (): void => {
    navigate(previous());
  };

  const radioOptionClicked = (event: RadioOptionClickEvent<string>): void => {
    if (
      !('target' in event && 'value' in event.target) ||
      !isValidClickEvent(event)
    ) {
      return;
    }

    formSubmit({ yearValue: event.target.value });
  };

  return (
    <div className="apply-search-year-page">
      <ThemeProvider theme={theme}>
        <Box sx={theme.pageLayout}>
          <header data-testid={locator.pages.applySearchYear.header.root}>
            <button
              aria-label="Previous step"
              data-testid={
                locator.pages.applySearchYear.header.previousStepButton
              }
              type="button"
              onClick={previousStep}
            >
              <KeyboardBackspaceRoundedIcon />
            </button>
            <button
              aria-label="Toggle drawer"
              data-testid={locator.pages.applySearchYear.header.drawerButton}
              type="button"
              onClick={() => {
                setDrawerOpen(true);
              }}
            >
              <MenuRoundedIcon />
            </button>
          </header>

          <div className="progress-container" role="complementary">
            <LinearProgress
              aria-label="Application completed percentage"
              data-testid={locator.pages.applySearchYear.progress.root}
              value={useWorkflowStore.getState().computed.progress}
              variant="determinate"
            />
          </div>

          <main>
            <form
              data-testid={locator.pages.applySearchYear.form.root}
              noValidate
              onSubmit={handleSubmit(formSubmit)}
            >
              <div className="form-content">
                <h1 data-testid={locator.pages.applySearchYear.form.h1}>
                  {t('ApplySearchYearPage.title')}
                </h1>
                <h2 data-testid={locator.pages.applySearchYear.form.h2}>
                  {t('ApplySearchYearPage.subtitle')}
                </h2>

                <div style={{ marginBottom: '3rem' }}>
                  <RadioGroup
                    defaultValue={year}
                    name="yearValue"
                    options={vehicleYears.map((yearValue, index) => ({
                      label: yearValue,
                      value: yearValue,
                      testId:
                        locator.pages.applySearchYear.form.getRadioOption(
                          index,
                        ),
                    }))}
                    register={register}
                    testId={locator.pages.applySearchYear.form.radioGroupRoot}
                    variant="stacked-compact"
                    watch={watch}
                    onClick={radioOptionClicked}
                  />
                </div>

                {/* allows form submission using the keyboard without a visible button*/}
                <button
                  aria-hidden="true"
                  data-testid={
                    locator.pages.applySearchYear.form.accessibilitySubmit
                  }
                  style={{ display: 'none' }}
                  type="submit"
                />
              </div>
              <footer data-testid={locator.pages.applySearchYear.footer.root}>
                {t('copyright', { year: new Date().getFullYear() })}
              </footer>
            </form>
          </main>
        </Box>

        <NavigationPane withDrawer withUserNameHeader />
      </ThemeProvider>
    </div>
  );
}

Component.displayName = 'ApplySearchYearPage';
